import type { SiteRagReport } from '../../types/siteRag';
import { reportDocumentModelV1Schema, reportRevisionReferenceV1Schema, presentationSnapshotV1Schema,
  type ReportDocumentModelV1, type PresentationSnapshotV1 } from '../../types/reportContractsV2';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { hashReportDocumentModelContent } from '../reportGeneration/reportDocumentIdentity';

/** No current owner site/SED or hydrated overlay can supply historical facts. */
export async function loadFrozenReportSummary(metadata: SiteRagReport, loadJson: (path: string) => Promise<unknown>): Promise<{
  report: SiteRagReport; document: ReportDocumentModelV1; presentation: PresentationSnapshotV1;
}> {
  const revision = reportRevisionReferenceV1Schema.parse(metadata.reportRevision);
  const modern = Boolean(revision.sourcePath && revision.sourceHash);
  const path = modern ? revision.sourcePath! : (metadata as SiteRagReport & { storagePath?: string }).storagePath;
  const bodySha = path?.match(/\/body\/([a-f0-9]{64})\.json$/)?.[1];
  const legacyImmutable = Boolean(bodySha && path?.endsWith(`/reports/${metadata.id}/revisions/${revision.revisionId}/body/${bodySha}.json`));
  if (!path || (!modern && !legacyImmutable)) throw new Error('The frozen bytes for this saved revision are unavailable.');
  const value = await loadJson(path);
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || hashCanonicalJson(value) !== (modern ? revision.sourceHash : bodySha)) throw new Error('The saved report failed its content check.');
  const frozen = value as SiteRagReport;
  const sourceRevision = frozen.reportRevision;
  if (frozen.id !== metadata.id || frozen.reportType !== metadata.reportType || frozen.siteId !== metadata.siteId
    || (!modern && (!sourceRevision || sourceRevision.revisionId !== revision.revisionId
      || sourceRevision.documentPath !== revision.documentPath || sourceRevision.documentHash !== revision.documentHash
      || sourceRevision.presentationPath !== revision.presentationPath || sourceRevision.presentationHash !== revision.presentationHash
      || sourceRevision.sourcePath !== revision.sourcePath || sourceRevision.sourceHash !== revision.sourceHash
      || hashCanonicalJson(sourceRevision.deliveryDecision) !== hashCanonicalJson(revision.deliveryDecision)))) {
    throw new Error('The frozen report belongs to a different saved revision.');
  }
  const document = reportDocumentModelV1Schema.parse(await loadJson(revision.documentPath));
  if (document.reportId !== frozen.id || document.reportType !== frozen.reportType || document.siteId !== frozen.siteId
    || document.revisionId !== revision.revisionId || document.contentHash !== revision.documentHash
    || hashReportDocumentModelContent(document) !== document.contentHash) throw new Error('The saved canonical document failed its identity check.');
  const presentation = presentationSnapshotV1Schema.parse(await loadJson(revision.presentationPath));
  const { presentationHash, ...content } = presentation;
  if (presentationHash !== revision.presentationHash || hashCanonicalJson(content) !== presentationHash) throw new Error('The saved presentation failed its content check.');
  const metaOwner = metadata as SiteRagReport & {
    reportOwnerUid?: string;
    siteOwnerUid?: string;
  };
  const pathOwnerUid = /^users\/([^/]+)\/reports\//.exec(path)?.[1];
  const resolvedOwnerUid =
    (typeof metadata._ownerUid === 'string' && metadata._ownerUid.trim() ? metadata._ownerUid.trim() : undefined) ??
    (typeof metaOwner.reportOwnerUid === 'string' && metaOwner.reportOwnerUid.trim() ? metaOwner.reportOwnerUid.trim() : undefined) ??
    (pathOwnerUid && pathOwnerUid.trim() ? pathOwnerUid.trim() : undefined);
  return {
    report: {
      ...frozen,
      reportRevision: revision,
      pdfDelivery: metadata.pdfDelivery,
      ...(metadata.analysisRevision !== undefined ? { analysisRevision: metadata.analysisRevision } : {}),
      ...(metadata.analysisRevisionId !== undefined ? { analysisRevisionId: metadata.analysisRevisionId } : {}),
      ...(metadata.analysisInputHash !== undefined ? { analysisInputHash: metadata.analysisInputHash } : {}),
      ...(metadata.analysisContentHash !== undefined ? { analysisContentHash: metadata.analysisContentHash } : {}),
      ...(metadata.analysisReadiness !== undefined ? { analysisReadiness: metadata.analysisReadiness } : {}),
      ...(metadata.analysisState !== undefined ? { analysisState: metadata.analysisState } : {}),
      ...(metadata.analysisRevisions !== undefined ? { analysisRevisions: metadata.analysisRevisions } : {}),
      ...(metadata.reportContentRevision !== undefined ? { reportContentRevision: metadata.reportContentRevision } : {}),
      ...(metadata.reportContentRevisionId !== undefined ? { reportContentRevisionId: metadata.reportContentRevisionId } : {}),
      ...(metadata.reportContentInputHash !== undefined ? { reportContentInputHash: metadata.reportContentInputHash } : {}),
      ...(metadata.reportContentHash !== undefined ? { reportContentHash: metadata.reportContentHash } : {}),
      ...(metadata.reportContentReadiness !== undefined ? { reportContentReadiness: metadata.reportContentReadiness } : {}),
      ...(metadata.reportContentState !== undefined ? { reportContentState: metadata.reportContentState } : {}),
      ...(resolvedOwnerUid ? { _ownerUid: resolvedOwnerUid } : {}),
      ...(typeof metaOwner.reportOwnerUid === 'string' && metaOwner.reportOwnerUid.trim()
        ? { reportOwnerUid: metaOwner.reportOwnerUid }
        : resolvedOwnerUid
          ? { reportOwnerUid: resolvedOwnerUid }
          : {}),
      ...(typeof metaOwner.siteOwnerUid === 'string' && metaOwner.siteOwnerUid.trim()
        ? { siteOwnerUid: metaOwner.siteOwnerUid }
        : {}),
    },
    document,
    presentation,
  };
}
